import { useCallback, useMemo, useRef, useState } from 'react';

import type { Layout } from 'react-grid-layout';

import { MIN_CARD_H, MIN_CARD_W } from '../_constants';

type PersistedState = {
  layout: Layout;
  hiddenItems: string[];
};

type Options = {
  storageKey: string;
  defaultLayout: Layout;
  defaultVisibleItems?: string[];
  isDraft?: boolean;
};

type Result = {
  layout: Layout;
  onLayoutChange: (newLayout: Layout) => void;
  resetLayout: () => void;
  hiddenItems: Set<string>;
  toggleItemVisibility: (id: string) => void;
  revertToStoredState: () => void;
  commitDraft: () => void;
};

const readPersistedValue = <T>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const writePersistedValue = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error(err);
  }
};

const readPersistedState = (
  storageKey: string,
  defaultLayout: Layout,
  defaultHiddenItems: string[]
): PersistedState => {
  const stored = readPersistedValue<PersistedState | Layout>(storageKey);
  if (!stored) {
    return { layout: defaultLayout, hiddenItems: defaultHiddenItems };
  }

  // Migrate old format (plain Layout array). Object payloads may omit
  // layout/hiddenItems — fall back to defaults so load never throws.
  const parsed: PersistedState = Array.isArray(stored)
    ? { layout: stored as Layout, hiddenItems: defaultHiddenItems }
    : {
        layout: Array.isArray((stored as PersistedState).layout)
          ? (stored as PersistedState).layout
          : defaultLayout,
        hiddenItems:
          (stored as PersistedState).hiddenItems ?? defaultHiddenItems,
      };

  const storedById = new Map(parsed.layout.map((item) => [item.i, item]));
  const previousIds = new Set(storedById.keys());
  const defaultIds = new Set(defaultLayout.map((item) => item.i));

  // Keep stored positions for existing items; fall back to default for new ones;
  // drop positions for removed items. Always apply minW/minH from defaults —
  // they are code-defined constraints and must not be overridden by stale storage.
  // Also clamp w/h up to current minW/minH — handles cases where a card's minimum
  // size was increased after the user last saved (e.g. card gained more content).
  let needsWrite = false;
  const reconciledLayout = defaultLayout.map((defaultItem) => {
    const storedItem = storedById.get(defaultItem.i);
    if (!storedItem) return defaultItem;
    const minW = defaultItem.minW ?? MIN_CARD_W;
    const minH = defaultItem.minH ?? MIN_CARD_H;
    const clampedW = Math.max(storedItem.w, minW);
    const clampedH = Math.max(storedItem.h, minH);
    if (clampedW !== storedItem.w || clampedH !== storedItem.h) {
      needsWrite = true;
    }
    return {
      ...storedItem,
      minW: defaultItem.minW,
      minH: defaultItem.minH,
      w: clampedW,
      h: clampedH,
    };
  });

  // Drop hidden ids that no longer exist. When the user already has a saved
  // layout, also hide newly added cards that are hidden by default (avoids a
  // storage-key bump every card add). Skip that when previousIds is empty —
  // e.g. e2e `{ layout: [], hiddenItems: [] }` means "show everything".
  const reconciledHiddenItems = [
    ...new Set([
      ...parsed.hiddenItems.filter((id) => defaultIds.has(id)),
      ...(previousIds.size > 0
        ? defaultHiddenItems.filter(
            (id) => defaultIds.has(id) && !previousIds.has(id)
          )
        : []),
    ]),
  ];

  const reconciledState: PersistedState = {
    layout: reconciledLayout,
    hiddenItems: reconciledHiddenItems,
  };
  if (needsWrite) writePersistedValue(storageKey, reconciledState);
  return reconciledState;
};

/**
 * Persists grid layout and card visibility to localStorage.
 * Falls back to defaultLayout on first load or when localStorage is unavailable.
 * Migrates the legacy plain-Layout format automatically.
 */
export const usePersistedLayout = ({
  storageKey,
  defaultLayout,
  defaultVisibleItems,
  isDraft = false,
}: Options): Result => {
  const defaultHiddenItemsRef = useRef<string[]>([]);
  defaultHiddenItemsRef.current = defaultVisibleItems
    ? defaultLayout
        .map((item) => item.i)
        .filter((id) => !defaultVisibleItems.includes(id))
    : [];

  const [state, setState] = useState<PersistedState>(() =>
    readPersistedState(storageKey, defaultLayout, defaultHiddenItemsRef.current)
  );

  const hiddenItems = useMemo(
    () => new Set(state.hiddenItems),
    [state.hiddenItems]
  );

  const layout = useMemo(
    () => state.layout.filter((item) => !hiddenItems.has(item.i)),
    [state.layout, hiddenItems]
  );

  const onLayoutChange = useCallback(
    (newVisibleLayout: Layout) => {
      setState((prev) => {
        // Merge updated visible positions back with the stored hidden positions
        const hiddenPositions = prev.layout.filter((item) =>
          prev.hiddenItems.includes(item.i)
        );
        const newFullLayout = [...newVisibleLayout, ...hiddenPositions];
        const newState: PersistedState = { ...prev, layout: newFullLayout };
        if (!isDraft) writePersistedValue(storageKey, newState);
        return newState;
      });
    },
    [isDraft, storageKey]
  );

  const resetLayout = useCallback(() => {
    const newState: PersistedState = {
      layout: defaultLayout,
      hiddenItems: defaultHiddenItemsRef.current,
    };
    setState(newState);
    if (!isDraft) writePersistedValue(storageKey, newState);
  }, [defaultLayout, isDraft, storageKey]);

  const toggleItemVisibility = useCallback(
    (id: string) => {
      setState((prev) => {
        const newHiddenItems = prev.hiddenItems.includes(id)
          ? prev.hiddenItems.filter((i) => i !== id)
          : [...prev.hiddenItems, id];
        const newState: PersistedState = {
          ...prev,
          hiddenItems: newHiddenItems,
        };
        if (!isDraft) writePersistedValue(storageKey, newState);
        return newState;
      });
    },
    [isDraft, storageKey]
  );

  const revertToStoredState = useCallback(() => {
    setState(
      readPersistedState(
        storageKey,
        defaultLayout,
        defaultHiddenItemsRef.current
      )
    );
  }, [storageKey, defaultLayout]);

  const commitDraft = useCallback(() => {
    setState((prev) => {
      writePersistedValue(storageKey, prev);
      return prev;
    });
  }, [storageKey]);

  return {
    layout,
    onLayoutChange,
    resetLayout,
    hiddenItems,
    toggleItemVisibility,
    revertToStoredState,
    commitDraft,
  };
};
